<script setup lang="ts">
import type { EmailVerificationResponse } from '@@/types/api';
import { useAuthApi } from '~/composables/api/use-auth-api';
import { useAuthStore } from '~/stores/use-auth-store';

/**
 * Self-contained email-verification step for any surface the backend's
 * `verified` middleware gates. The caller decides WHEN to show it (it owns the
 * `email_verified_at` check); this component owns the whole way OUT of it:
 * send a code → enter it → verify → refresh the profile so the caller's own
 * gate condition flips and the real content renders IN PLACE. No reload, no
 * navigation away.
 *
 * Deliberately product-agnostic — `title` and `reason` are the only copy the
 * host supplies.
 */
withDefaults(
  defineProps<{
    /** Headline. Say what the verification unblocks. */
    title?: string;
    /** One line explaining why a verified email is required here. */
    reason?: string;
  }>(),
  {
    title: 'Verify your email',
    reason: 'This step needs a verified email address.',
  },
);

const emit = defineEmits<{
  /** Fired once the profile has been refreshed and the user is verified. */
  verified: [];
}>();

const authStore = useAuthStore();
const authApi = useAuthApi();

// Mirrors the backend rule: `required|string|size:6|regex:/^\d{6}$/`.
const CODE_PATTERN = /^\d{6}$/;

const code = ref('');
const error = ref<string | null>(null);
const isSending = ref(false);
const isVerifying = ref(false);

/**
 * The address the code was ACTUALLY sent to, captured at send time. The copy
 * before a send must never claim one happened — that false claim is the bug
 * this component replaces — so the "we sent a code" branch is driven by this
 * being non-null rather than by the profile email.
 */
const sentTo = ref<string | null>(null);

const email = computed(() => authStore.user?.email ?? 'your email address');
const isBusy = computed(() => isSending.value || isVerifying.value);

/**
 * `{ success: false, message: 'Email is already verified' }` is not a failure —
 * it means the profile in hand is stale. Treat it exactly like a success.
 * The message text is the only signal the endpoint gives.
 */
function isAlreadyVerified(res: EmailVerificationResponse): boolean {
  return /already verified/i.test(res.message ?? '');
}

/**
 * Both endpoints are throttled (`otp-send` / `otp-verify`), so a 429 is an
 * ordinary outcome and gets its own actionable message rather than being
 * flattened into "something went wrong".
 */
function transportMessage(e: unknown, fallback: string): string {
  const err = e as { name?: string; status?: number; message?: string };
  if (err?.name === 'ApiError') {
    if (err.status === 429) {
      return 'Too many attempts — wait a minute and try again.';
    }
    if (err.message) return err.message;
  }
  return fallback;
}

async function unlock(): Promise<void> {
  // Repopulates `email_verified_at`, which flips the host's gate condition.
  await authStore.fetchUser();
  emit('verified');
}

async function sendCode(): Promise<void> {
  if (isBusy.value) return;
  error.value = null;
  isSending.value = true;
  try {
    const res = await authApi.sendEmailVerificationCode();
    if (res.success) {
      sentTo.value = authStore.user?.email ?? null;
      return;
    }
    if (isAlreadyVerified(res)) {
      await unlock();
      return;
    }
    error.value =
      res.message ?? 'We could not send a verification code. Please try again.';
  } catch (e: unknown) {
    error.value = transportMessage(
      e,
      'We could not send a verification code. Please try again.',
    );
  } finally {
    isSending.value = false;
  }
}

async function verifyCode(): Promise<void> {
  if (isBusy.value) return;

  const entered = code.value.trim();
  if (!CODE_PATTERN.test(entered)) {
    error.value = 'Enter the 6-digit code from the email.';
    return;
  }

  error.value = null;
  isVerifying.value = true;
  try {
    const res = await authApi.verifyEmailCode({ code: entered });
    if (res.success || isAlreadyVerified(res)) {
      await unlock();
      return;
    }
    // Stay on the step — the code is re-enterable and resendable.
    error.value = res.message ?? 'Invalid verification code.';
  } catch (e: unknown) {
    error.value = transportMessage(
      e,
      'We could not check that code. Please try again.',
    );
  } finally {
    isVerifying.value = false;
  }
}
</script>

<template>
  <v-card
    variant="outlined"
    rounded="lg"
    class="pa-6 text-center"
    data-testid="email-verification-gate"
  >
    <v-icon size="48" color="on-surface-variant" class="mb-3">
      mdi-email-alert-outline
    </v-icon>
    <h2 class="text-h6 mb-2">{{ title }}</h2>

    <!-- Before a send: describe the mechanism honestly and offer to start it. -->
    <template v-if="sentTo === null">
      <p class="text-body-2 text-on-surface-variant mb-4">
        {{ reason }} We'll email a 6-digit code to
        <span class="font-weight-medium">{{ email }}</span>
        to confirm it's yours.
      </p>
      <v-btn
        color="primary"
        variant="flat"
        :loading="isSending"
        data-testid="send-code-button"
        @click="sendCode"
      >
        Send me a code
      </v-btn>
    </template>

    <!-- After a send: the claim is now true, so it is safe to state it. -->
    <template v-else>
      <p class="text-body-2 text-on-surface-variant mb-4">
        We sent a 6-digit code to
        <span class="font-weight-medium">{{ sentTo }}</span
        >. Enter it below to finish verifying.
      </p>

      <v-text-field
        v-model="code"
        label="6-digit code"
        placeholder="123456"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="6"
        variant="outlined"
        density="comfortable"
        class="mx-auto"
        style="max-width: 220px"
        data-testid="code-input"
        @keyup.enter="verifyCode"
      />

      <div class="d-flex flex-wrap justify-center ga-2">
        <v-btn
          color="primary"
          variant="flat"
          :loading="isVerifying"
          :disabled="isSending"
          data-testid="verify-code-button"
          @click="verifyCode"
        >
          Verify email
        </v-btn>
        <v-btn
          variant="text"
          :loading="isSending"
          :disabled="isVerifying"
          data-testid="resend-code-button"
          @click="sendCode"
        >
          Send a new code
        </v-btn>
      </div>
    </template>

    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      density="compact"
      class="mt-4 text-left"
      data-testid="verification-error"
    >
      {{ error }}
    </v-alert>
  </v-card>
</template>
